import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ContasAReceberResumoDTO, ContasAReceberResumoPaginado } from '../../../../models/financeiro.model';
import { FinanceiroService } from '../../../../services/financeiro.service';

@Component({
  selector: 'app-contas-a-receber',
  imports: [CommonModule, FormsModule],
  templateUrl: './contas-a-receber.component.html',
  styleUrl: './contas-a-receber.component.css',
})
export class ContasAReceberComponent implements OnInit {
  contasReceber: ContasAReceberResumoDTO[] = [];

  /** Valor digitado no campo, ainda não submetido. */
  searchTerm = '';
  /** Termo efetivamente aplicado na busca; é o que acompanha a paginação. */
  private termoAplicado = '';

  currentPage = 0;
  itemsPerPage = 10;
  totalPages = 1;
  totalElements = 0;
  isLoading = false;

  maxPagesToShow = 5;
  startPage = 0;
  endPage = 0;
  pages: number[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly pageSubject = new Subject<number>();

  constructor(
    private financeiroService: FinanceiroService,
    private toastr: ToastrService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // switchMap garante que apenas a resposta da última requisição seja aplicada,
    // evitando que uma página antiga sobrescreva a atual.
    this.pageSubject
      .pipe(
        tap(() => {
          this.isLoading = true;
          this.cdr.detectChanges();
        }),
        switchMap((page) =>
          this.financeiroService.getContasAReceberResumo(page, this.itemsPerPage, this.termoAplicado).pipe(
            catchError(() => {
              this.toastr.error('Não foi possível carregar as contas a receber.');
              return of(null);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((resultado) => {
        this.aplicarResultado(resultado);
        this.isLoading = false;
        this.cdr.detectChanges();
      });

    this.pageSubject.next(0);
  }

  setPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.currentPage) {
      return;
    }

    this.pageSubject.next(page);
  }

  pesquisar(): void {
    this.termoAplicado = this.searchTerm.trim();
    this.pageSubject.next(0);
  }

  limparPesquisa(): void {
    this.searchTerm = '';
    this.termoAplicado = '';
    this.pageSubject.next(0);
  }

  visualizarConta(idVenda: number): void {
    this.router.navigate(['/home', 'financeiro-conta-a-receber-manutencao', idVenda]);
  }

  private aplicarResultado(resultado: ContasAReceberResumoPaginado | null): void {
    if (!resultado) {
      return;
    }

    this.contasReceber = resultado.content ?? [];
    this.currentPage = resultado.number ?? 0;
    this.totalElements = resultado.totalElements ?? 0;
    this.totalPages = Math.max(1, resultado.totalPages ?? 1);
    this.updatePageNumbers();
  }

  private updatePageNumbers(): void {
    const halfPages = Math.floor(this.maxPagesToShow / 2);
    this.startPage = Math.max(0, this.currentPage - halfPages);
    this.endPage = Math.min(this.totalPages - 1, this.startPage + this.maxPagesToShow - 1);

    if (this.endPage - this.startPage + 1 < this.maxPagesToShow) {
      this.startPage = Math.max(0, this.endPage - this.maxPagesToShow + 1);
    }

    this.pages = Array.from({ length: this.endPage - this.startPage + 1 }, (_, i) => this.startPage + i);
  }

}
